/**
 * Audit event category visibility filter
 *
 * EVENT_CATEGORIES is auto-generated from api/utils/AuditEventDescriptions.ts.
 * Events with no backend emitter are already excluded at generation time (the
 * `unused` flag — see METALARK-773), so this module handles the two visibility
 * rules that depend on runtime state:
 *
 *   1. Statically hidden categories (HIDDEN_CATEGORIES) — categories whose
 *      backend integration isn't wired up yet:
 *      - MFA: schema (UserMfaConfig), middleware (PermissionService.requireMFA),
 *        and AuditLogger.logMfaEvent all exist, but the only caller of
 *        logMfaEvent is api/services/MFAService.ts, which nothing imports —
 *        the service is unwired so MFA_* audit rows are never written. Remove
 *        this entry (and ideally MFAService.ts) once the feature is wired up.
 *
 *   2. Role-based visibility (METALARK-773) — events listed in the generated
 *      SYSTEM_ADMIN_ONLY_EVENTS (e.g. campaign management) are only shown to
 *      SYSTEM_ADMIN. Pass `{ isSystemAdmin: true }` to include them. The
 *      backend enforces the same rule on queries, so this is presentation
 *      only — hiding them here just keeps the filter honest for other roles.
 *
 * Add new static entries to HIDDEN_CATEGORIES by category key and document
 * the reason — these are not stable; we want each hidden category to come
 * back as soon as it's real.
 */

import { EVENT_CATEGORIES, SYSTEM_ADMIN_ONLY_EVENTS } from './auditEventCategories.js';

const HIDDEN_CATEGORIES = new Set([
    'MFA',
]);

const SYSTEM_ADMIN_ONLY_SET = new Set(SYSTEM_ADMIN_ONLY_EVENTS);

/**
 * Return EVENT_CATEGORIES entries with hidden categories filtered out,
 * preserving the auto-generated ordering. Use this everywhere the UI
 * iterates categories — never iterate EVENT_CATEGORIES directly when
 * rendering UI controls.
 *
 * Without options (or with isSystemAdmin false) system-admin-only events
 * are removed, and categories left with no events are dropped entirely —
 * the safe default while the caller's role is still loading.
 *
 * @param {{ isSystemAdmin?: boolean }} [options]
 */
export function getVisibleEventCategoryEntries(options = {}) {
    const { isSystemAdmin = false } = options;
    return Object.entries(EVENT_CATEGORIES)
        .filter(([key]) => !HIDDEN_CATEGORIES.has(key))
        .map(([key, category]) => {
            const events = isSystemAdmin
                ? category.events
                : (category.events || []).filter((event) => !SYSTEM_ADMIN_ONLY_SET.has(event));
            return [key, { ...category, events }];
        })
        .filter(([, category]) => category.events.length > 0);
}

/**
 * Whether the given category key is currently exposed in the UI for the
 * caller's role. Useful for guards in places that already have the key in
 * hand (e.g. derived `activeCategoryKey` in the URL).
 *
 * @param {string} categoryKey
 * @param {{ isSystemAdmin?: boolean }} [options]
 */
export function isCategoryVisible(categoryKey, options = {}) {
    if (HIDDEN_CATEGORIES.has(categoryKey)) return false;
    const category = EVENT_CATEGORIES[categoryKey];
    if (!category) return false;
    const { isSystemAdmin = false } = options;
    if (isSystemAdmin) return true;
    return (category.events || []).some((event) => !SYSTEM_ADMIN_ONLY_SET.has(event));
}
